import { useCallback, useEffect, useMemo } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { StyleTemplate } from "@/features/studio-ai/types/studio-ai.types"

import { ArrowLeft, Save } from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable"
import { M365InfoModal } from "@/features/ai-explorer/components/m365-info-modal"
import {
  clearStyleTemplateDraft,
  useStyleTemplateDraftAutosave,
} from "@/features/studio-ai/hooks/use-style-template-draft-autosave"
import {
  useCreateStyleTemplate,
  useUpdateStyleTemplate,
} from "@/features/studio-ai/hooks/use-style-templates"
import { useStyleTemplateEditorStore } from "@/features/studio-ai/stores/style-template-editor-store"

import { StyleTemplateChat } from "./style-template-chat"
import { StyleTemplatePreview } from "./style-template-preview"
import { useStyleTemplateChat } from "../../hooks/use-style-template-chat"

type StyleTemplateCreatorProps = {
  onBack: () => void
  onSaved: () => void
  mode?: "create" | "edit"
  template?: StyleTemplate | null
}

export function StyleTemplateCreator({
  onBack,
  onSaved,
  mode = "create",
  template,
}: StyleTemplateCreatorProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi.styleTemplates" })
  const { mutateAsync: createTemplate, isPending: isSaving } = useCreateStyleTemplate()
  const { mutateAsync: updateTemplate, isPending: isUpdating } = useUpdateStyleTemplate()
  const title = useStyleTemplateEditorStore((state) => state.title)
  const description = useStyleTemplateEditorStore((state) => state.description)
  const promptFragment = useStyleTemplateEditorStore((state) => state.promptFragment)
  const brandColors = useStyleTemplateEditorStore((state) => state.brandColors)
  const visibility = useStyleTemplateEditorStore((state) => state.visibility)
  const pendingAiUpdate = useStyleTemplateEditorStore((state) => state.pendingAiUpdate)
  const restoreCandidate = useStyleTemplateEditorStore((state) => state.restoreCandidate)
  const autosaveStatus = useStyleTemplateEditorStore((state) => state.autosaveStatus)
  const syncStatus = useStyleTemplateEditorStore((state) => state.syncStatus)
  const lastDraftSavedAt = useStyleTemplateEditorStore((state) => state.lastDraftSavedAt)
  const recommendedAdherence = useStyleTemplateEditorStore((state) => state.recommendedAdherence)
  const setRecommendedAdherence = useStyleTemplateEditorStore(
    (state) => state.setRecommendedAdherence
  )
  const setVisibility = useStyleTemplateEditorStore((state) => state.setVisibility)
  const setTitle = useStyleTemplateEditorStore((state) => state.setTitle)
  const setDescription = useStyleTemplateEditorStore((state) => state.setDescription)
  const setPromptFragment = useStyleTemplateEditorStore((state) => state.setPromptFragment)
  const setBrandColors = useStyleTemplateEditorStore((state) => state.setBrandColors)
  const setIsDirty = useStyleTemplateEditorStore((state) => state.setIsDirty)
  const stageAiUpdate = useStyleTemplateEditorStore((state) => state.stageAiUpdate)
  const applyAiUpdate = useStyleTemplateEditorStore((state) => state.applyAiUpdate)
  const discardAiUpdate = useStyleTemplateEditorStore((state) => state.discardAiUpdate)
  const applyRestoreCandidate = useStyleTemplateEditorStore((state) => state.applyRestoreCandidate)
  const discardRestoreCandidate = useStyleTemplateEditorStore(
    (state) => state.discardRestoreCandidate
  )

  useStyleTemplateDraftAutosave({ skipRestore: mode === "edit" })

  const handleExtractedData = useCallback(
    (data: {
      title?: string
      description?: string
      prompt_fragment?: string
      brand_colors?: string[]
    }) => {
      stageAiUpdate(data)
    },
    [stageAiUpdate]
  )

  const {
    messages,
    isStreaming,
    streamingContent,
    files,
    sessionType,
    showWebSearchOption,
    showM365Option,
    showServiceDeskOption,
    showNetboxOption,
    hasAgentModeFeature,
    isPromptsFeatureEnabled,
    showHighReasoning,
    enableAgent,
    enableWebSearch,
    enableM365,
    enableServiceDesk,
    enableNetbox,
    enableHighReasoning,
    setEnableAgent,
    setEnableWebSearch,
    setEnableM365,
    setEnableServiceDesk,
    setEnableNetbox,
    setEnableHighReasoning,
    showM365Info,
    handleM365Approve,
    handleM365Decline,
    handleM365Dismiss,
    sendMessage,
    cancelStream,
    addFiles,
    removeFile,
  } = useStyleTemplateChat({ onExtractedData: handleExtractedData })

  useEffect(() => {
    if (!template) return

    useStyleTemplateEditorStore.setState({
      title: template.title,
      description: template.description,
      promptFragment: template.prompt_fragment,
      brandColors: template.brand_colors,
      visibility: mode === "edit" ? template.visibility : "personal",
      recommendedAdherence: template.recommended_adherence || "inspired",
      isDirty: false,
      pendingAiUpdate: null,
      restoreCandidate: null,
      autosaveStatus: "idle",
      syncStatus: "idle",
    })
  }, [mode, template])

  const handleSave = async () => {
    const normalizedTitle = title.trim()
    const normalizedDescription = description.trim()
    const normalizedPromptFragment = promptFragment.trim()

    if (!normalizedTitle) {
      toast.error(t("titleRequired"))
      return
    }

    try {
      if (mode === "edit" && template?.id) {
        await updateTemplate({
          id: template.id,
          data: {
            title: normalizedTitle,
            description: normalizedDescription,
            prompt_fragment: normalizedPromptFragment,
            visibility,
            brand_colors: brandColors,
            recommended_adherence: recommendedAdherence,
          },
        })
      } else {
        await createTemplate({
          title: normalizedTitle,
          description: normalizedDescription,
          prompt_fragment: normalizedPromptFragment,
          visibility,
          media_type: "both",
          preview_image_url: undefined,
          brand_colors: brandColors,
          recommended_adherence: recommendedAdherence,
        })
        clearStyleTemplateDraft()
      }
      setIsDirty(false)
      onSaved()
    } catch {
      // Error handled by mutation
    }
  }

  const autosaveText = useMemo(() => {
    if (autosaveStatus === "saving") {
      return t("sync.savingLocal", { defaultValue: "Saving draft locally..." })
    }
    if (autosaveStatus === "error") {
      return t("sync.saveError", { defaultValue: "Local draft save failed" })
    }
    if (syncStatus === "syncing") {
      return t("sync.syncing", { defaultValue: "Syncing draft to cloud..." })
    }
    if (syncStatus === "sync_error") {
      return t("sync.syncError", { defaultValue: "Saved locally, cloud sync pending" })
    }
    if (syncStatus === "synced" && lastDraftSavedAt) {
      return t("sync.syncedAt", {
        defaultValue: "Draft synced {{time}}",
        time: new Date(lastDraftSavedAt).toLocaleTimeString(),
      })
    }
    if (autosaveStatus === "saved" && lastDraftSavedAt) {
      return t("sync.savedLocalAt", {
        defaultValue: "Draft saved locally {{time}}",
        time: new Date(lastDraftSavedAt).toLocaleTimeString(),
      })
    }
    return t("sync.enabled", { defaultValue: "Draft autosave enabled" })
  }, [autosaveStatus, lastDraftSavedAt, syncStatus, t])

  return (
    <div className="flex h-[calc(100svh-140px)] flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <Button size="icon" variant="ghost" onClick={onBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">{t("creator.title")}</h2>
            <p className="text-muted-foreground text-xs">{autosaveText}</p>
          </div>
        </div>
        <Button
          disabled={!title.trim() || isSaving || isUpdating}
          onClick={() => void handleSave()}
        >
          <Save className="mr-2 h-4 w-4" />
          {isSaving || isUpdating ? t("saving") : t("save")}
        </Button>
      </div>

      {pendingAiUpdate && (
        <div className="bg-primary/5 border-primary/20 mx-4 mt-3 rounded-lg border p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <Badge variant="secondary">AI update</Badge>
              <p className="text-sm font-medium">
                {t("aiUpdate.title", { defaultValue: "New style suggestion ready for review" })}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={discardAiUpdate}>
                {t("aiUpdate.discard", { defaultValue: "Discard" })}
              </Button>
              <Button size="sm" onClick={applyAiUpdate}>
                {t("aiUpdate.apply", { defaultValue: "Apply" })}
              </Button>
            </div>
          </div>
          <div className="mt-2 space-y-1 text-xs">
            {pendingAiUpdate.title && (
              <p>
                <span className="text-muted-foreground font-medium">
                  {t("fields.title", { defaultValue: "Title" })}:
                </span>{" "}
                {pendingAiUpdate.title}
              </p>
            )}
            {pendingAiUpdate.description && (
              <p>
                <span className="text-muted-foreground font-medium">
                  {t("fields.description", { defaultValue: "Description" })}:
                </span>{" "}
                {pendingAiUpdate.description}
              </p>
            )}
            {pendingAiUpdate.prompt_fragment && (
              <p className="line-clamp-3">
                <span className="text-muted-foreground font-medium">
                  {t("fields.promptFragment", { defaultValue: "Prompt" })}:
                </span>{" "}
                {pendingAiUpdate.prompt_fragment}
              </p>
            )}
            {pendingAiUpdate.brand_colors && pendingAiUpdate.brand_colors.length > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="text-muted-foreground font-medium">
                  {t("fields.brandColors", { defaultValue: "Colors" })}:
                </span>
                {pendingAiUpdate.brand_colors.map((color) => (
                  <span
                    key={color}
                    className="inline-block h-4 w-4 rounded-full border"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Split-screen content */}
      <ResizablePanelGroup className="mt-3 flex-1" direction="horizontal">
        <ResizablePanel defaultSize={50} minSize={30}>
          <div className="flex h-full flex-col">
            <div className="border-b px-4 py-2">
              <h3 className="text-sm font-medium">{t("creator.chatTitle")}</h3>
            </div>
            <StyleTemplateChat
              enableAgent={enableAgent}
              enableHighReasoning={enableHighReasoning}
              enableM365={enableM365}
              enableNetbox={enableNetbox}
              enableServiceDesk={enableServiceDesk}
              enableWebSearch={enableWebSearch}
              files={files}
              hasAgentModeFeature={hasAgentModeFeature}
              isPromptsFeatureEnabled={isPromptsFeatureEnabled}
              isStreaming={isStreaming}
              messages={messages}
              sessionType={sessionType}
              showHighReasoning={showHighReasoning}
              showM365Option={showM365Option}
              showNetboxOption={showNetboxOption}
              showServiceDeskOption={showServiceDeskOption}
              showWebSearchOption={showWebSearchOption}
              streamingContent={streamingContent}
              onAgentChange={setEnableAgent}
              onCancel={cancelStream}
              onFilesAdded={addFiles}
              onHighReasoningChange={setEnableHighReasoning}
              onM365Change={setEnableM365}
              onNetboxChange={setEnableNetbox}
              onRemoveFile={removeFile}
              onSendMessage={(text) => void sendMessage(text)}
              onServiceDeskChange={setEnableServiceDesk}
              onWebSearchChange={setEnableWebSearch}
            />
          </div>
        </ResizablePanel>

        <ResizableHandle withHandle />

        <ResizablePanel defaultSize={50} minSize={25}>
          <div className="flex h-full flex-col">
            <div className="border-b px-4 py-2">
              <h3 className="text-sm font-medium">{t("creator.previewTitle")}</h3>
            </div>
            <StyleTemplatePreview
              brandColors={brandColors}
              description={description}
              previewImages={[]}
              promptFragment={promptFragment}
              recommendedAdherence={recommendedAdherence}
              title={title}
              visibility={visibility}
              onBrandColorsChange={setBrandColors}
              onDescriptionChange={setDescription}
              onPromptFragmentChange={setPromptFragment}
              onRecommendedAdherenceChange={setRecommendedAdherence}
              onTitleChange={setTitle}
              onVisibilityChange={(nextVisibility) => {
                setVisibility(nextVisibility)
              }}
            />
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>

      <AlertDialog
        open={Boolean(restoreCandidate)}
        onOpenChange={(open) => !open && discardRestoreCandidate()}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("restoreDraft.title", { defaultValue: "Restore saved draft?" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("restoreDraft.description", {
                defaultValue:
                  "We found a {{source}} draft from {{time}}. Do you want to restore it?",
                source:
                  restoreCandidate?.source === "server"
                    ? t("restoreDraft.cloud", { defaultValue: "cloud" })
                    : t("restoreDraft.local", { defaultValue: "local" }),
                time: restoreCandidate?.updatedAt
                  ? new Date(restoreCandidate.updatedAt).toLocaleString()
                  : "",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={discardRestoreCandidate}>
              {t("restoreDraft.startFresh", { defaultValue: "Start fresh" })}
            </AlertDialogCancel>
            <AlertDialogAction onClick={applyRestoreCandidate}>
              {t("restoreDraft.restore", { defaultValue: "Restore draft" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <M365InfoModal
        open={showM365Info}
        onApprove={handleM365Approve}
        onDecline={handleM365Decline}
        onDismiss={handleM365Dismiss}
      />
    </div>
  )
}
